/** The condition the upload URL is signed with, sent so the request matches its own signature. */
const CREATE_ONLY_IF_NONE_MATCH = "*";

/** R2's answer when an object already exists under a create-only PUT's key. */
const PRECONDITION_FAILED_STATUS = 412;

/**
 * Send one file's bytes straight to media storage, over a presigned PUT.
 *
 * The sibling of `upload-to-convex-storage.ts`, and it differs in exactly two ways. The verb is
 * PUT, not POST — the URL is signed for that method and R2 refuses anything else — and there is
 * **no response to parse**: a presigned PUT answers with an empty body, so the object's key is
 * not something this learns. The key comes from the mutation that minted the URL, which is
 * where it has to come from anyway: a client that named its own keys could claim another
 * project's object.
 *
 * Progress, abort and error semantics are deliberately identical, because the upload queue
 * drives both paths through the same status machine.
 */
export function uploadToR2(
  uploadUrl: string,
  file: File,
  onProgress: (progress: number) => void = () => {},
  signal?: AbortSignal
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Upload aborted", "AbortError"));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    // The signature covers the host and the key, not this header, so it travels unsigned and
    // R2 stores it as the object's content type — which is what lets registration verify the
    // type against what the store observed rather than against what the client claimed.
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
    // This one *is* signed. The URL is minted create-only, so `if-none-match` is in its
    // `X-Amz-SignedHeaders` and the request is rejected outright without it: the header is how
    // the browser meets the condition, not something it chooses to impose.
    xhr.setRequestHeader("If-None-Match", CREATE_ONLY_IF_NONE_MATCH);

    const abortUpload = () => {
      xhr.abort();
      reject(new DOMException("Upload aborted", "AbortError"));
    };
    signal?.addEventListener("abort", abortUpload, { once: true });

    const cleanup = () => {
      signal?.removeEventListener("abort", abortUpload);
    };

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      const nextProgress = Math.max(0, Math.min(1, event.loaded / event.total));
      onProgress(nextProgress);
    };

    xhr.onabort = () => {
      cleanup();
      reject(new DOMException("Upload aborted", "AbortError"));
    };
    xhr.onerror = () => {
      cleanup();
      reject(new Error(`Network error while uploading ${file.name}`));
    };
    xhr.onload = () => {
      cleanup();
      // A `412` says the key already holds an object, and the only way to reach that through the
      // product is a PUT whose response was lost: the bytes landed, the browser never heard so,
      // and this is the retry. The key is minted per upload and its ledger row is refused to any
      // second registration, so what is under it is this file — registration's own HEAD is what
      // states the size and type, and it verifies them against R2 rather than against this call.
      if (xhr.status === PRECONDITION_FAILED_STATUS) {
        resolve();
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(`Upload failed (${xhr.status}) for ${file.name}`));
        return;
      }
      resolve();
    };

    xhr.send(file);
  });
}
